import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  useTheme,
  alpha,
} from '@mui/material';
import { AlertTriangle, Trash2, CheckCircle2, Info, X } from 'lucide-react';

/**
 * ConfirmDialog - Standardized confirmation modal.
 * Completely backward-compatible with all legacy calls while providing modern elevated UI,
 * keyboard accessibility, and clear destructive action differentiation.
 */
export default function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title = 'Confirm Action',
  content,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  severity = 'error', // 'error' | 'warning' | 'info' | 'success'
  isLoading = false,
  loading,
  confirmColor,
  icon: CustomIcon,
  children,
  warningNote,
  maxWidth = 'xs',
}) {
  const theme = useTheme();
  const isBusy = isLoading || loading || false;
  const messageText = content || description || 'Are you sure you want to proceed?';

  const severityConfigs = {
    error: {
      color: theme.palette.error.main,
      lighter: theme.palette.error.lighter || alpha(theme.palette.error.main, 0.12),
      icon: Trash2,
      btnColor: 'error',
    },
    warning: {
      color: theme.palette.warning.main,
      lighter: theme.palette.warning.lighter || alpha(theme.palette.warning.main, 0.12),
      icon: AlertTriangle,
      btnColor: 'warning',
    },
    info: {
      color: theme.palette.info.main,
      lighter: theme.palette.info.lighter || alpha(theme.palette.info.main, 0.12),
      icon: Info,
      btnColor: 'primary',
    },
    success: {
      color: theme.palette.success.main,
      lighter: theme.palette.success.lighter || alpha(theme.palette.success.main, 0.12),
      icon: CheckCircle2,
      btnColor: 'success',
    },
  };

  const config = severityConfigs[severity] || severityConfigs.error;
  const IconComponent = CustomIcon || config.icon;
  const effectiveBtnColor = confirmColor || config.btnColor;

  const titleId = 'confirm-dialog-title';
  const descId = 'confirm-dialog-description';

  return (
    <Dialog
      open={open}
      onClose={isBusy ? undefined : onClose}
      maxWidth={maxWidth}
      fullWidth
      aria-labelledby={titleId}
      aria-describedby={descId}
      slotProps={{
        paper: {
          sx: {
            p: 0,
            overflow: 'hidden',
          },
        },
      }}
    >
      <DialogTitle
        id={titleId}
        component="div"
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          p: { xs: 2.5, sm: 3 },
          pb: 1.5,
          gap: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: config.lighter,
              color: config.color,
              flexShrink: 0,
            }}
          >
            <IconComponent size={22} strokeWidth={2.2} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              component="h2"
              sx={{ fontWeight: 800, fontSize: '1.05rem', letterSpacing: '-0.01em', lineHeight: 1.25 }}
            >
              {title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Please review before confirming
            </Typography>
          </Box>
        </Box>

        <IconButton
          aria-label="Close confirmation dialog"
          onClick={onClose}
          disabled={isBusy}
          size="small"
          sx={{
            color: 'text.secondary',
            p: 0.5,
            borderRadius: 1.5,
            '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.06), color: 'text.primary' },
          }}
        >
          <X size={18} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 2.5, sm: 3 }, py: 1.5 }}>
        {typeof messageText === 'string' ? (
          <DialogContentText
            id={descId}
            sx={{
              color: 'text.secondary',
              fontSize: '0.875rem',
              lineHeight: 1.6,
            }}
          >
            {messageText}
          </DialogContentText>
        ) : (
          <Box id={descId}>{messageText}</Box>
        )}

        {children}

        {warningNote && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: 1.5,
              bgcolor: alpha(config.color, 0.08),
              border: `1px solid ${alpha(config.color, 0.25)}`,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <AlertTriangle size={16} color={config.color} />
            <Typography variant="caption" sx={{ color: config.color, fontWeight: 600 }}>
              {warningNote}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2.5, sm: 3 },
          py: 2,
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.default, 0.4) : alpha(theme.palette.grey[50], 0.8),
          gap: 1.25,
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={isBusy}
          sx={{ minWidth: 88, fontWeight: 600 }}
        >
          {cancelText}
        </Button>
        <Button
          variant="contained"
          color={effectiveBtnColor}
          onClick={onConfirm}
          disabled={isBusy}
          startIcon={isBusy ? <CircularProgress size={16} color="inherit" /> : null}
          sx={{
            minWidth: 108,
            fontWeight: 700,
            boxShadow: `0 2px 8px 0 ${alpha(config.color, 0.35)}`,
          }}
        >
          {isBusy ? 'Processing...' : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
